import { REPORT_MENU, REPORT_TYPE_LABELS } from '@/app/dashboards/reportConstants';
import { CHILI_PIPER_FORMS_MAP } from '@/app/settings/constants';
import communityReport1x from '@/assets/images/dashboards/noReports/community-report/1x.png';
import communityReport2x from '@/assets/images/dashboards/noReports/community-report/2x.png';
import communityReport3x from '@/assets/images/dashboards/noReports/community-report/3x.png';
import competitiveReport1x from '@/assets/images/dashboards/noReports/competitive-report/1x.png';
import competitiveReport2x from '@/assets/images/dashboards/noReports/competitive-report/2x.png';
import competitiveReport3x from '@/assets/images/dashboards/noReports/competitive-report/3x.png';
import creatorsEarnedReport1x from '@/assets/images/dashboards/noReports/creators-earned-report/1x.png';
import creatorsEarnedReport2x from '@/assets/images/dashboards/noReports/creators-earned-report/2x.png';
import creatorsEarnedReport3x from '@/assets/images/dashboards/noReports/creators-earned-report/3x.png';
import industryEarnedReport1x from '@/assets/images/dashboards/noReports/industry-earned-report/1x.png';
import industryEarnedReport2x from '@/assets/images/dashboards/noReports/industry-earned-report/2x.png';
import industryEarnedReport3x from '@/assets/images/dashboards/noReports/industry-earned-report/3x.png';
import ownedReport1x from '@/assets/images/dashboards/noReports/owned-report/1x.png';
import ownedReport2x from '@/assets/images/dashboards/noReports/owned-report/2x.png';
import ownedReport3x from '@/assets/images/dashboards/noReports/owned-report/3x.png';
import socialAdvertisingReport1x from '@/assets/images/dashboards/noReports/social-advertising-report/1x.png';
import socialAdvertisingReport2x from '@/assets/images/dashboards/noReports/social-advertising-report/2x.png';
import socialAdvertisingReport3x from '@/assets/images/dashboards/noReports/social-advertising-report/3x.png';
import socialListeningReport1x from '@/assets/images/dashboards/noReports/social-listening-report/1x.png';
import socialListeningReport2x from '@/assets/images/dashboards/noReports/social-listening-report/2x.png';
import socialListeningReport3x from '@/assets/images/dashboards/noReports/social-listening-report/3x.png';
import previewBar from '@/assets/images/dashboards/preview/bar.png';
import previewDoughnut from '@/assets/images/dashboards/preview/doughnut.png';
import previewHorizontalBar from '@/assets/images/dashboards/preview/horizontal-bar.png';
import previewLine from '@/assets/images/dashboards/preview/line.png';
import previewStackedBar from '@/assets/images/dashboards/preview/stacked-bar.png';
import { PRODUCT_MAP } from '@/models/auth/customer-plan.enum';
import { BRAND, ORGANIZATION, ORGANIZATION_USER, USER } from '@/models/auth/permissions.enum';
import { getEnumValue } from '@/models/enum.utils';
import { srcSet } from '@/utils/srcset';

export const METRIC_TYPE_REPORT_KEYS = {
  CONTENT_REPORT: 'content_report',
  GRAPH_REPORT: 'graph_report',
  METRICS_REPORT: 'metrics_report',
};

export const GRAPH_REPORT_TYPES = Object.freeze({
  BAR: 'BAR',
  LINE: 'LINE',
  STACKED_BAR: 'STACKED_BAR',
  DOUGHNUT: 'DOUGHNUT',
  HORIZONTAL_BAR: 'HORIZONTAL_BAR',
});

export const REPORT_UPGRADE_MAP = Object.freeze({
  SOCIAL_ADVERTISING: {
    bannerButtonName: 'Try Premium Analytics',
    chiliPiper: CHILI_PIPER_FORMS_MAP.socialListening.contact,
    product: PRODUCT_MAP.PREMIUM_ANALYTICS,
    popupName: 'startTrial',
  },
  INDUSTRY: {
    bannerButtonName: 'Try Premium Analytics',
    chiliPiper: CHILI_PIPER_FORMS_MAP.premiumAnalytics.contact,
    product: PRODUCT_MAP.PREMIUM_ANALYTICS,
    popupName: 'startTrial',
  },
  COMPETITIVE: {
    bannerButtonName: 'Try Premium Analytics',
    chiliPiper: CHILI_PIPER_FORMS_MAP.premiumAnalytics.contact,
    product: PRODUCT_MAP.PREMIUM_ANALYTICS,
    popupName: 'startTrial',
  },
  LISTENING: {
    bannerButtonName: 'Try Social Listening',
    chiliPiper: CHILI_PIPER_FORMS_MAP.socialListening.contact,
    product: PRODUCT_MAP.SOCIAL_LISTENING,
    popupName: 'startTrial',
  },
});

export const REPORT_CATEGORY_OPTIONS = {
  OWNED: {
    id: 'owned_report',
    name: 'Owned Report',
    filterKey: 'owned',
    dataSources: ['DB', 'ES', 'IG_LIVE_API'],
    description: `Measure your brand's performance across all connected social channels`,
    bannerButtonName: 'Create Report',
    bannerSrc: ownedReport1x,
    bannerSrcset: srcSet(ownedReport1x, ownedReport2x, ownedReport3x),
  },
  COMPETITIVE: {
    id: 'competitive_report',
    name: 'Competitive Report',
    filterKey: 'competitive',
    dataSources: ['DB', 'ES'],
    description: `View key trends in your competitors' performance and compare your performance`,
    bannerButtonName: 'Create Report',
    bannerSrc: competitiveReport1x,
    bannerSrcset: srcSet(competitiveReport1x, competitiveReport2x, competitiveReport3x),
    brandPermissions: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
    userPermissions: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
    upgradeOptions: REPORT_UPGRADE_MAP.COMPETITIVE,
  },
  SOCIAL_ADVERTISING: {
    id: 'social_advertising_report',
    name: 'Social Advertising Report',
    filterKey: 'social_advertising',
    dataSources: ['DB', 'ES'],
    description: 'Monitor and compare ad campaign performance across brands',
    bannerButtonName: 'Create Report',
    bannerSrc: socialAdvertisingReport1x,
    bannerSrcset: srcSet(
      socialAdvertisingReport1x,
      socialAdvertisingReport2x,
      socialAdvertisingReport3x,
    ),
    brandPermissions: [BRAND.ADS.CAN_ACCESS_ADS],
    userPermissions: [USER.ADS.CAN_ACCESS_ADS],
    upgradeOptions: REPORT_UPGRADE_MAP.SOCIAL_ADVERTISING,
  },
  INDUSTRY: {
    id: 'industry_report',
    name: 'Industry Report',
    filterKey: 'industry',
    dataSources: ['DB', 'ES'],
    description: 'Compare your content performance to the industry average',
    bannerButtonName: 'Create Report',
    bannerSrc: industryEarnedReport1x,
    bannerSrcset: srcSet(industryEarnedReport1x, industryEarnedReport2x, industryEarnedReport3x),
    brandPermissions: [BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS],
    upgradeOptions: REPORT_UPGRADE_MAP.INDUSTRY,
  },
  EARNED: {
    id: 'earned_report',
    name: 'Earned Report',
    filterKey: 'earned',
    dataSources: ['DB', 'ES'],
    description:
      'Track the frequency and performance of brand mentions in creator and user-generated posts',
    bannerButtonName: 'Create Report',
    bannerSrc: creatorsEarnedReport1x,
    bannerSrcset: srcSet(creatorsEarnedReport1x, creatorsEarnedReport2x, creatorsEarnedReport3x),
  },
  COMMUNITY: {
    id: 'community_report',
    name: 'Community Report',
    filterKey: 'community',
    dataSources: ['COMMUNITY'],
    description: 'Measure your brand and team performance in Community across your social channels',
    bannerButtonName: 'Create Report',
    bannerSrc: communityReport1x,
    bannerSrcset: srcSet(communityReport1x, communityReport2x, communityReport3x),
  },
  SOCIAL_LISTENING: {
    id: 'social_listening_report',
    name: 'Social Listening Report',
    filterKey: 'social_listening',
    dataSources: ['LISTENING'],
    description: 'Measure Topic performance across social channels',
    bannerButtonName: 'Create Report',
    bannerSrc: socialListeningReport1x,
    bannerSrcset: srcSet(socialListeningReport1x, socialListeningReport2x, socialListeningReport3x),
    organizationUserPermissions: [ORGANIZATION.LISTENING.CAN_ACCESS_SOCIAL_LISTENING],
    organizationPermissions: [ORGANIZATION_USER.LISTENING.CAN_ACCESS_SOCIAL_LISTENING],
    upgradeOptions: REPORT_UPGRADE_MAP.LISTENING,
    stepsRequired: true,
    additionalStepDialog: {
      group: 'global',
      header: 'Create a Topic?',
      message: `You don't have any topics yet. To create a Social Listening Report, you'll need to create a topic first.`,
      acceptLabel: 'Create Topic',
      acceptSeverity: 'primary',
    },
  },
  TSI: {
    id: 'tsi_report',
    name: 'Total Social Impact Report',
    filterKey: 'tsi',
    dataSources: ['DB', 'ES'],
    description: `Monitor your social performance according to Dash's proprietary metrics`,
    bannerButtonName: 'Create Report',
    bannerSrc: ownedReport1x,
    bannerSrcset: srcSet(ownedReport1x, ownedReport2x, ownedReport3x),
    lockedMetric: {
      value: 'TOTAL_TSI',
      label: 'Total Social Impact (TSI)',
    },
    lockedChannels: true,
  },
};

export const REPORT_CATEGORY_TO_FILTER_KEY_MAP = Object.freeze({
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: 'community',
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: 'listening',
});

export const DEFAULT_METRICS_BY_REPORT_CATEGORY = Object.freeze({
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: {
    [METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT]: 'ENGAGEMENTS',
  },
});

export const REPORT_CATEGORY_TO_DATA_SOURCE_MAP = Object.freeze(
  Object.values(REPORT_CATEGORY_OPTIONS).reduce((acc, { id, dataSources }) => {
    acc[id] = dataSources;
    return acc;
  }, {}),
);

const BASE_REPORT_CONFIG = Object.freeze({
  TOTAL_METRIC: {
    width: 1,
    height: 8,
    oldHeight: 2,
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.METRICS_REPORT,
  },
  TOTAL_GROUPED_METRIC: {
    width: 3,
    height: 16,
    oldHeight: 4,
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.METRICS_REPORT,
  },
  GRAPH: {
    text: 'Line Graph',
    icon: 'graph',
    width: 3,
    height: 16,
    oldHeight: 4,
    previewImage: previewLine,
    previewImageAlt: 'line graph preview image',
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  BAR_METRIC: {
    text: 'Single Bar Chart',
    icon: 'stackedGraph',
    width: 3,
    height: 16,
    oldHeight: 4,
    previewImage: previewBar,
    previewImageAlt: 'bar chart preview image',
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  STACKED_BAR_METRIC: {
    text: 'Stacked Bar Chart',
    icon: 'stackedGraph',
    previewImage: previewStackedBar,
    previewImageAlt: 'stacked bar chart preview image',
    width: 3,
    height: 16,
    oldHeight: 4,
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  DOUGHNUT_METRIC: {
    text: 'Doughnut Chart',
    previewImage: previewDoughnut,
    previewImageAlt: 'doughnut chart preview image',
    previewImageClass: '!w-fit',
    width: 3,
    height: 16,
    oldHeight: 4,
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  VERTICAL_BAR_METRIC: {
    text: 'Vertical Bar Chart',
    previewImage: previewBar,
    previewImageAlt: 'vertical bar chart preview image',
    width: 3,
    height: 16,
    oldHeight: 4,
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  HORIZONTAL_BAR_METRIC: {
    text: 'Horizontal Bar Chart',
    previewImage: previewHorizontalBar,
    previewImageAlt: 'horizontal bar chart preview image',
    width: 3,
    height: 16,
    oldHeight: 4,
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  },
  CONTENT: {
    width: 6,
    height: 20,
    oldHeight: 4,
    menuItems: {
      ...REPORT_MENU.REPORT,
    },
    metricTypeReportKey: METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT,
  },
});
// We'll need to remove the component property from the REPORTS object
export const REPORTS = Object.freeze({
  SINGLE_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_METRIC,
    value: 'SINGLE_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GroupedMetricReportPanel',
      },

      preview: {
        name: 'GroupedMetricReportPanel',
      },
    },
  },
  TOTAL_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_METRIC,
    value: 'TOTAL_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GroupedMetricReportPanel',
      },
      preview: {
        name: 'GroupedMetricReportPanel',
      },
    },
  },
  CREATOR_TOTAL_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_METRIC,
    value: 'CREATOR_TOTAL_METRIC',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'GroupedMetricReportPanel',
      },

      preview: {
        name: 'GroupedMetricReportPanel',
      },
    },
  },
  ADS_TOTAL_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_METRIC,
    value: 'ADS_TOTAL_METRIC',
    category: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING,
    components: {
      report: {
        name: 'GroupedMetricReportPanel',
      },
      preview: {
        name: 'GroupedMetricReportPanel',
      },
    },
  },
  COMPETITIVE_TOTAL_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_METRIC,
    value: 'COMPETITIVE_TOTAL_METRIC',
    category: REPORT_CATEGORY_OPTIONS.COMPETITIVE,
  },
  MULTI_BRAND_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'MULTI_BRAND_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },

      preview: {
        width: 1,
        height: 3,
        name: 'GroupedMetricReportPanel',
      },
    },
  },
  TOTAL_GROUPED_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'TOTAL_GROUPED_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  ADS_TOTAL_GROUPED_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'ADS_TOTAL_GROUPED_METRIC',
    category: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  MULTI_METRIC_MEDIA_TYPE: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'MULTI_METRIC_MEDIA_TYPE',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  COMPETITIVE_MULTI_BRAND_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'COMPETITIVE_MULTI_BRAND_METRIC',
    category: REPORT_CATEGORY_OPTIONS.COMPETITIVE,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  CREATOR_TOTAL_GROUPED_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'CREATOR_TOTAL_GROUPED_METRIC',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  // TODO Remove CONTENT type once we run migration to set report type to OWNED or UGC
  //  (See src/app/dashboards/store/actions.js, and tests/unit/factories/dashboards/report.js also)
  CONTENT: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'CONTENT',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'ContentReportPanel',
      },
    },
  },
  CONTENT_OWNED: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'CONTENT_OWNED',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'ContentReportPanel',
      },
    },
  },
  CONTENT_OWNED_STORIES: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'CONTENT_OWNED_STORIES',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'ContentReportPanel',
      },
    },
  },
  CONTENT_UGC: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'CONTENT_UGC',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'ContentReportPanel',
      },
    },
  },
  COMPETITIVE_CONTENT: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'COMPETITIVE_CONTENT',
    category: REPORT_CATEGORY_OPTIONS.COMPETITIVE,
    components: {
      report: {
        name: 'CompetitiveContentReportPanel',
      },
    },
  },
  CREATOR_CONTENT: {
    ...BASE_REPORT_CONFIG.CONTENT,
    value: 'CREATOR_CONTENT',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'ContentReportPanel',
      },
    },
  },
  GRAPH: {
    ...BASE_REPORT_CONFIG.GRAPH,
    value: 'GRAPH',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  COMPETITIVE_GRAPH: {
    ...BASE_REPORT_CONFIG.GRAPH,
    value: 'COMPETITIVE_GRAPH',
    category: REPORT_CATEGORY_OPTIONS.COMPETITIVE,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  CREATOR_LINE_GRAPH: {
    ...BASE_REPORT_CONFIG.GRAPH,
    value: 'CREATOR_LINE_GRAPH',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  ADS_GRAPH: {
    ...BASE_REPORT_CONFIG.GRAPH,
    value: 'ADS_GRAPH',
    category: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  BENCHMARK_METRIC: {
    ...BASE_REPORT_CONFIG.TOTAL_GROUPED_METRIC,
    value: 'BENCHMARK_METRIC',
    category: REPORT_CATEGORY_OPTIONS.INDUSTRY,
    components: {
      report: {
        name: 'MetricReportTablePanel',
      },
    },
  },
  BENCHMARK_GRAPH: {
    ...BASE_REPORT_CONFIG.GRAPH,
    value: 'BENCHMARK_GRAPH',
    category: REPORT_CATEGORY_OPTIONS.INDUSTRY,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  DOUGHNUT_METRIC: {
    ...BASE_REPORT_CONFIG.DOUGHNUT_METRIC,
    value: 'DOUGHNUT_METRIC',
    category: REPORT_CATEGORY_OPTIONS.TSI,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  BAR_METRIC: {
    ...BASE_REPORT_CONFIG.BAR_METRIC,
    value: 'BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  VERTICAL_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.BAR_METRIC,
    value: 'BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
  },
  COMPETITIVE_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.BAR_METRIC,
    value: 'COMPETITIVE_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.COMPETITIVE,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  ADS_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.BAR_METRIC,
    value: 'ADS_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  CREATOR_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.BAR_METRIC,
    value: 'CREATOR_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.EARNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  STACKED_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.STACKED_BAR_METRIC,
    value: 'STACKED_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  STACKED_BAR_METRIC_TIME_SERIES: {
    ...BASE_REPORT_CONFIG.STACKED_BAR_METRIC,
    value: 'STACKED_BAR_METRIC_TIME_SERIES',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  ADS_STACKED_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.STACKED_BAR_METRIC,
    value: 'ADS_STACKED_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  HORIZONTAL_BAR_METRIC: {
    ...BASE_REPORT_CONFIG.HORIZONTAL_BAR_METRIC,
    value: 'HORIZONTAL_BAR_METRIC',
    category: REPORT_CATEGORY_OPTIONS.OWNED,
    components: {
      report: {
        name: 'GraphReportPanel',
      },
    },
  },
  HEADER: {
    value: 'HEADER',
    width: 6,
    height: 2,
    widget: true,
    menuItems: {
      ...REPORT_MENU.HEADER,
    },

    components: {
      report: {
        name: 'HeaderReportPanel',
      },
    },
  },
  NOTE: {
    value: 'NOTE',
    width: 2,
    height: 8,
    oldHeight: 2,
    widget: true,
    menuItems: {
      ...REPORT_MENU.NOTE,
    },
    components: {
      report: {
        name: 'NoteReportPanel',
      },
    },
  },
  DASHBOARD_SUMMARY: {
    value: 'DASHBOARD_SUMMARY',
    width: 1,
    height: 8,
    oldHeight: 2,
    components: {
      report: {
        name: 'VisionAiReportPanelPlaceholder',
      },
    },
  },
});

export const LEGACY_REPORT_CATEGORY_OPTIONS = Object.freeze({
  ALL: {
    value: 'ALL',
    text: 'All Reports',
  },
  OWNED: {
    value: 'OWNED',
    text: 'Owned Reports',
  },
  COMPETITIVE: {
    value: 'COMPETITIVE',
    text: 'Competitive Reports',
    permissions: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
    userPermissions: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  },
  INDUSTRY: {
    value: 'INDUSTRY',
    text: 'Benchmark Reports',
    permissions: [BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS],
  },
  SOCIAL_ADVERTISING: {
    value: 'SOCIAL_ADVERTISING',
    text: 'Social Advertising',
    permissions: [BRAND.ADS.CAN_ACCESS_ADS],
    userPermissions: [USER.ADS.CAN_ACCESS_ADS],
  },
});

export const REPORT_FINISH_BUTTON_TEXT = {
  ADD_REPORT: 'Add Report',
  Next: 'Next',
};

export const REPORT_FORM_EMIT = {
  INPUT: 'update:modelValue',
  VALIDATION: 'validation',
};

export const SINGLE_BRAND_REPORT_TYPES = Object.freeze([
  REPORTS.CONTENT_OWNED.value,
  REPORTS.CONTENT_OWNED_STORIES.value,
  REPORTS.CONTENT_UGC.value,
  REPORTS.CONTENT.value,
  REPORTS.CREATOR_CONTENT.value,
]);

export const MULTI_CHANNEL_REPORT_TYPES = Object.freeze([
  REPORTS.GRAPH.value,
  REPORTS.TOTAL_METRIC.value,
  REPORTS.TOTAL_GROUPED_METRIC.value,
]);

export function isWidgetReport(reportType) {
  const key = getEnumValue(reportType);
  return REPORTS?.[key]?.widget;
}

export const getReportConfigProp = (report, property) => {
  const reportKey = getEnumValue(report);
  const reportConfig = REPORTS[reportKey];
  return reportConfig?.[property];
};

export const OWNED_CONTENT_REPORT_TYPES = [
  REPORTS.CONTENT_OWNED.value,
  REPORTS.CONTENT_OWNED_STORIES.value,
  REPORTS.CONTENT_UGC.value,
  REPORTS.CONTENT.value,
];

export const OWNED_REPORT_TYPES = [
  ...OWNED_CONTENT_REPORT_TYPES,
  REPORTS.GRAPH.value,
  REPORTS.TOTAL_METRIC.value,
  REPORTS.BAR_METRIC.value,
];

export const COMPETITIVE_CONTENT_REPORT_TYPES = [REPORTS.COMPETITIVE_CONTENT.value];

export const CONTENT_REPORT_TYPES = [
  ...OWNED_CONTENT_REPORT_TYPES,
  ...COMPETITIVE_CONTENT_REPORT_TYPES,
  REPORTS.CREATOR_CONTENT.value,
];

export function isContentReport(reportType) {
  return CONTENT_REPORT_TYPES.includes(reportType);
}

export function isMultiMediaMetricReport(reportType) {
  return reportType === REPORTS.MULTI_METRIC_MEDIA_TYPE.value;
}

export const COMPETITIVE_REPORTS = [
  REPORTS.COMPETITIVE_CONTENT.value,
  REPORTS.COMPETITIVE_GRAPH.value,
  REPORTS.COMPETITIVE_MULTI_BRAND_METRIC.value,
  REPORTS.COMPETITIVE_BAR_METRIC.value,
  REPORTS.DASHBOARD_SUMMARY.value,
];

export function isCompetitiveReport(reportType) {
  return COMPETITIVE_REPORTS.includes(reportType);
}

export const CREATOR_REPORTS = [
  REPORTS.CREATOR_BAR_METRIC.value,
  REPORTS.CREATOR_CONTENT.value,
  REPORTS.CREATOR_LINE_GRAPH.value,
  REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value,
  REPORTS.CREATOR_TOTAL_METRIC.value,
];

export const METRIC_TABLE_REPORTS = [
  REPORTS.MULTI_BRAND_METRIC.value,
  REPORTS.MULTI_METRIC_MEDIA_TYPE.value,
  REPORTS.COMPETITIVE_MULTI_BRAND_METRIC.value,
  REPORTS.BENCHMARK_METRIC.value,
  REPORTS.TOTAL_GROUPED_METRIC.value,
  REPORTS.ADS_TOTAL_GROUPED_METRIC.value,
  REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value,
];

export const GRAPH_REPORTS = [
  REPORTS.GRAPH.value,
  REPORTS.COMPETITIVE_GRAPH.value,
  REPORTS.BENCHMARK_GRAPH.value,
  REPORTS.ADS_GRAPH.value,
  REPORTS.CREATOR_LINE_GRAPH.value,
];

export const LINE_GRAPH_REPORT_TYPES = Object.freeze({
  GRAPH: 'GRAPH',
  GRAPH_MULTI_BRAND: 'GRAPH_MULTI_BRAND',
  GRAPH_MULTI_BRAND_SINGLE_MEDIA_TYPE: 'GRAPH_MULTI_BRAND_SINGLE_MEDIA_TYPE',
  GRAPH_SINGLE_BRAND_SINGLE_MEDIA_TYPE: 'GRAPH_SINGLE_BRAND_SINGLE_MEDIA_TYPE',
  GRAPH_SINGLE_BRAND_MULTI_MEDIA_TYPE: 'GRAPH_SINGLE_BRAND_MULTI_MEDIA_TYPE',
  BENCHMARK_GRAPH: 'BENCHMARK_GRAPH',
  ADS_GRAPH: 'ADS_GRAPH',
  COMPETITIVE_GRAPH: 'COMPETITIVE_GRAPH',
  CREATOR_LINE_GRAPH: 'CREATOR_LINE_GRAPH',
});

export const COMPETITIVE_GRAPH_CHART_TYPES = Object.freeze({
  LINE_CHART: REPORTS.COMPETITIVE_GRAPH,
  SINGLE_COLUMN_GRAPH: REPORTS.COMPETITIVE_BAR_METRIC,
});

export const GRAPH_CHART_TYPES = Object.freeze({
  LINE_CHART: REPORTS.GRAPH,
  SINGLE_COLUMN_GRAPH: REPORTS.BAR_METRIC,
  STACKED_COLUMN_GRAPH: REPORTS.STACKED_BAR_METRIC,
});

export const SINGLE_METRIC_REPORT_TYPE = Object.freeze({
  SINGLE_METRIC: 'SINGLE_METRIC',
  TOTAL_METRIC: 'TOTAL_METRIC',
  ADS_TOTAL_METRIC: 'ADS_TOTAL_METRIC',
  CREATOR_TOTAL_METRIC: 'CREATOR_TOTAL_METRIC',
});

export const BENCHMARK_REPORTS = [REPORTS.BENCHMARK_METRIC.value, REPORTS.BENCHMARK_GRAPH.value];

export const BAR_CHART_REPORTS = [
  REPORTS.BAR_METRIC.value,
  REPORTS.HORIZONTAL_BAR_METRIC.value,
  REPORTS.COMPETITIVE_BAR_METRIC.value,
  REPORTS.ADS_BAR_METRIC.value,
  REPORTS.ADS_STACKED_BAR_METRIC.value,
  REPORTS.STACKED_BAR_METRIC.value,
  REPORTS.STACKED_BAR_METRIC_TIME_SERIES.value,
  REPORTS.CREATOR_BAR_METRIC.value,
];

export const LINE_CHART_REPORTS = [...Object.values(LINE_GRAPH_REPORT_TYPES)];

export const STACKED_BAR_CHART_REPORTS = [
  REPORTS.STACKED_BAR_METRIC.value,
  REPORTS.ADS_STACKED_BAR_METRIC.value,
  REPORTS.STACKED_BAR_METRIC_TIME_SERIES.value,
];
export const TOTAL_REPORT_TYPES = [
  REPORTS.TOTAL_METRIC.value,
  REPORTS.TOTAL_GROUPED_METRIC.value,
  REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value,
];

export const CHANNEL_TO_CHANNELS_CONVERSION_REPORT_TYPES = [
  REPORTS.SINGLE_METRIC.value,
  REPORTS.MULTI_BRAND_METRIC.value,
  REPORTS.MULTI_METRIC_MEDIA_TYPE.value,
];

export const WIDGET_REPORT_TYPES = [REPORTS.HEADER.value, REPORTS.NOTE.value];

export const ADS_REPORT_TYPES = [
  REPORTS.ADS_TOTAL_METRIC.value,
  REPORTS.ADS_TOTAL_GROUPED_METRIC.value,
  REPORTS.ADS_GRAPH.value,
  REPORTS.ADS_BAR_METRIC.value,
  REPORTS.ADS_STACKED_BAR_METRIC.value,
];

export const REPORT_LABEL_TYPES = {
  BRAND: 'brand',
  COMPETITOR: 'competitor',
};

export const REPORT_TAG_TYPES = {
  BRAND: 'brand',
  COMPETITOR: 'competitor',
  CONTENT: 'content',
};

export const REPORT_TITLES = {
  METRICS_REPORT: 'Metric Report',
  CONTENT_REPORT: 'Content Report',
  GRAPH_REPORT: 'Graph Report',
  COMPETITIVE_GRAPH_REPORT: 'Competitive Graph Report',
  COMPETITIVE_METRIC_REPORT: 'Competitive Metric Report',
  COMPETITIVE_CONTENT_REPORT: 'Competitive Content Report',
};

export const REPORT_DESCRIPTIONS = {
  ADS_ACCOUNT_CONNECTION: 'No ad account connection',
  AGGREGATE_BY: 'Select report breakdown',
  BRANDS: 'Select brands to report on',
  CAMPAIGNS: 'Select your ad accounts and campaigns',
  COMPETITORS: 'Select competitors to report on',
  CHANNEL: 'Select social media channel',
  CHANNELS: 'Select social media channels',
  CONTENT_TAGS: 'Do you want to report on all content or tagged content?',
  CONTENT_TYPE: 'Select either Owned or User Generated content',
  INDUSTRY: 'Select which industry and follower size to report on',
  METRIC: 'Select a metric to report on',
  MEDIA_TYPE: 'Select which media type to report on',
  MEDIA_TYPES: 'Select which media types to report on',
  VISUALIZATION: 'Select report visualization',
  SORT_ORDER: 'Select a sort order',

  [REPORT_TITLES.GRAPH_REPORT]: {
    REPORT_VISUALIZATION_HEADER: 'Select report visualization',
    REPORT_AGGREGATION_HEADER: 'Select report breakdown',
  },
  [REPORT_TITLES.METRICS_REPORT]: {
    REPORT_VISUALIZATION_HEADER: 'Select report visualization',
    REPORT_AGGREGATION_HEADER: 'Select report breakdown',
  },
  [REPORT_TITLES.CONTENT_REPORT]: {
    REPORT_VISUALIZATION_HEADER: 'Select report visualization',
    REPORT_AGGREGATION_HEADER: 'Select a sort order',
  },
};

export const EXCLUDE_REPORT_TYPES_FROM_EXPANSION = [
  REPORTS.SINGLE_METRIC.value,
  REPORTS.TOTAL_METRIC.value,
  REPORTS.DASHBOARD_SUMMARY.value,
];
export const REPORT_CATEGORY_TO_TYPES_MAP = Object.freeze({
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: [
    { value: REPORTS.TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
    { value: REPORTS.CONTENT_OWNED.value, label: REPORT_TYPE_LABELS.CONTENT },
  ],
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: [
    { value: REPORTS.TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
  ],
  [REPORT_CATEGORY_OPTIONS.EARNED.id]: [
    { value: REPORTS.TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
    { value: REPORTS.CONTENT_UGC.value, label: REPORT_TYPE_LABELS.CONTENT },
  ],
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id]: [
    { value: REPORTS.COMPETITIVE_MULTI_BRAND_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.COMPETITIVE_GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
    { value: REPORTS.COMPETITIVE_CONTENT.value, label: REPORT_TYPE_LABELS.CONTENT },
  ],
  [REPORT_CATEGORY_OPTIONS.INDUSTRY.id]: [
    { value: REPORTS.BENCHMARK_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.BENCHMARK_GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
  ],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id]: [
    { value: REPORTS.ADS_TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.ADS_GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    { value: REPORTS.TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
  ],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    { value: REPORTS.TOTAL_METRIC.value, label: REPORT_TYPE_LABELS.METRIC },
    { value: REPORTS.GRAPH.value, label: REPORT_TYPE_LABELS.GRAPH },
    { value: REPORTS.CONTENT.value, label: REPORT_TYPE_LABELS.CONTENT },
  ],
});
export const PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP = Object.freeze({
  OWNED: REPORT_CATEGORY_OPTIONS.OWNED.id,
  COMPETITIVE: REPORT_CATEGORY_OPTIONS.COMPETITIVE.id,
  AD: REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id,
  INDUSTRY: REPORT_CATEGORY_OPTIONS.INDUSTRY.id,
  UGC: REPORT_CATEGORY_OPTIONS.EARNED.id,
  CREATOR: REPORT_CATEGORY_OPTIONS.EARNED.id,
  COMMUNITY: REPORT_CATEGORY_OPTIONS.COMMUNITY.id,
  TSI: REPORT_CATEGORY_OPTIONS.TSI.id,
  LISTENING: REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id,
});

export const ALL_GRAPH_REPORTS = Object.freeze([
  ...GRAPH_REPORTS,
  ...BAR_CHART_REPORTS,
  REPORTS.DOUGHNUT_METRIC.value,
]);

export const ALL_INDUSTRY_REPORTS = Object.freeze([
  REPORTS.BENCHMARK_METRIC.value,
  REPORTS.BENCHMARK_GRAPH.value,
]);

export const ALL_METRIC_REPORTS = Object.freeze([
  REPORTS.TOTAL_METRIC.value,
  REPORTS.SINGLE_METRIC.value,
  REPORTS.ADS_TOTAL_METRIC.value,
  ...METRIC_TABLE_REPORTS,
]);

export const COMPETITIVE_METRIC_REPORTS = Object.freeze([
  REPORTS.COMPETITIVE_MULTI_BRAND_METRIC.value,
  REPORTS.COMPETITIVE_TOTAL_METRIC.value,
]);

export const CREATOR_AGGREGATE_REPORT_TYPES = Object.freeze([
  REPORTS.CREATOR_BAR_METRIC.value,
  REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value,
  REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value,
]);

export const REPORTS_CATEGORIES_WITHOUT_BRAND_SELECTION = Object.freeze([
  REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id,
]);
